// Settings › Referral program — settings/referral (moved from marketing/referralManagement.js).
import { Share2 } from 'lucide-react';
import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Row } from 'reactstrap';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';
import { Field, SettingsCard, StatusPill } from './SettingsLayout';

const DEFAULTS = { active: true, referrerReward: 0, referredReward: 0, expiration: 30 };

const ReferralSettings = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    firestore().collection('settings').doc('referral').get().then((snap) => {
      if (snap.exists) {
        const d = snap.data();
        setSettings({
          active: d.active !== false,
          referrerReward: Number(d.referrerReward) || 0,
          referredReward: Number(d.referredReward) || 0,
          expiration: Number(d.expiration) || 30,
        });
        setUpdatedAt(d.updatedAt || null);
      }
    }).catch(() => createNotification('error', 'Failed to load referral settings', 'Settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = useCallback((field, value) => { setSettings((prev) => ({ ...prev, [field]: value })); setDirty(true); }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        active: settings.active,
        referrerReward: Number(settings.referrerReward) || 0,
        referredReward: Number(settings.referredReward) || 0,
        expiration: Number(settings.expiration) || 30,
        updatedAt: Date.now(),
      };
      await firestore().collection('settings').doc('referral').set(payload, { merge: true });
      setUpdatedAt(payload.updatedAt);
      setDirty(false);
      createNotification('success', 'Referral settings saved successfully', 'Settings');
    } catch (error) {
      console.error('Error saving referral settings:', error);
      createNotification('error', 'Failed to save referral settings', 'Settings');
    } finally { setSaving(false); }
  }, [settings]);

  if (loading) return <div className="loading" />;

  const footer = (
    <>
      <span className="st-foot-note">{updatedAt ? `Last saved ${new Date(Number(updatedAt)).toLocaleString()}` : ''}{dirty ? ' · unsaved changes' : ''}</span>
      <Button color="primary" disabled={saving || !dirty} onClick={handleSave}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </>
  );

  return (
    <SettingsCard
      title="Referral program"
      description="Wallet credit for customers who bring a friend, and for the friend on their first booking."
      status={<StatusPill on={settings.active} onLabel="Active" offLabel="Disabled" />}
      footer={footer}
    >
      <Row>
        <Col lg="6" className="mb-4 mb-lg-0">
          <div className="st-switch-row">
            <div className="st-switch-text">
              <b>Referral program {settings.active ? 'is active' : 'is disabled'}</b>
              <span>While disabled, referral links stop granting credit.</span>
            </div>
            <Switch className="custom-switch custom-switch-primary" checked={settings.active} onChange={(checked) => handleChange('active', checked)} />
          </div>
          <Field label="Referrer reward (AED)" help="Amount the existing user earns when their referred friend completes their first booking.">
            <Input type="number" min="0" step="1" value={settings.referrerReward} onChange={(e) => handleChange('referrerReward', e.target.value)} />
          </Field>
          <Field label="Referred user reward (AED)" help="Amount the new user receives as wallet credit when they accept the referral link.">
            <Input type="number" min="0" step="1" value={settings.referredReward} onChange={(e) => handleChange('referredReward', e.target.value)} />
          </Field>
          <Field label="Expiration (days)" help="Number of days before referral wallet credits expire.">
            <Input type="number" min="1" step="1" value={settings.expiration} onChange={(e) => handleChange('expiration', e.target.value)} />
          </Field>
        </Col>
        <Col lg="6">
          <div className="st-section-title">Preview</div>
          <div className="st-preview">
            <Share2 size={28} style={{ color: '#10b981', marginBottom: 8 }} />
            <h5 style={{ fontWeight: 700, marginBottom: 6 }}>Refer a Friend</h5>
            <p style={{ fontSize: '0.9rem', color: '#333', marginBottom: 0, lineHeight: 1.5 }}>
              You earn <strong style={{ color: '#10b981' }}>AED {Number(settings.referrerReward || 0).toFixed(2)}</strong> and your friend gets{' '}
              <strong style={{ color: '#3b82f6' }}>AED {Number(settings.referredReward || 0).toFixed(2)}</strong> on their first booking!
            </p>
            <p className="text-muted mt-1 mb-0" style={{ fontSize: '0.75rem' }}>Credits expire after {settings.expiration || 30} days</p>
          </div>
          <div className="mt-3">
            <h6 style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.85rem' }}>How it works</h6>
            {[
              'User shares their referral link',
              <>Friend gets <strong>AED {Number(settings.referredReward || 0).toFixed(2)}</strong> instantly</>,
              'Friend completes first booking',
              <>Referrer gets <strong>AED {Number(settings.referrerReward || 0).toFixed(2)}</strong></>,
            ].map((step, i) => (
              <div key={i} className="d-flex align-items-center mb-2">
                <span style={{ borderRadius: '50%', width: 24, height: 24, minWidth: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#3b82f6', color: '#fff', fontWeight: 600, fontSize: '0.7rem', marginRight: 10 }}>{i + 1}</span>
                <span style={{ fontSize: '0.85rem' }}>{step}</span>
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </SettingsCard>
  );
};

export default ReferralSettings;
